import axios from 'axios';

// Use REACT_APP_BACKEND_URL if available, otherwise try BACKEND_URL, or fallback to current origin
const API_BASE = process.env.REACT_APP_BACKEND_URL || process.env.BACKEND_URL || window.location.origin;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mlx_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ⚡ Intercepteur de réponse : gère les 401 (token expiré) globalement.
// Si un appel API renvoie 401, on purge le token et on redirige vers /login
// (sauf si on est déjà sur /login ou /auth/*, ou si on utilise l'API publique du portail client).
let _redirectingToLogin = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    if (status === 401 && !_redirectingToLogin) {
      const path = window.location.pathname;
      // ⚠️ Les routes du portail client sont en FRANÇAIS : /portail (et /api/portail/).
      // Un préfixe '/portal' anglais ne matche pas et provoquait la déconnexion du
      // cabinet dès qu'un client se trompait de mot de passe sur le portail.
      const isPublicPage = path === '/login' || path.startsWith('/auth/') || path.startsWith('/portail');
      // N'intercepte pas les endpoints du portail client (qui ont leur propre gestion d'auth)
      const isPortalCall = requestUrl.includes('/api/portail/');
      if (!isPublicPage && !isPortalCall) {
        _redirectingToLogin = true;
        try {
          localStorage.removeItem('mlx_token');
          localStorage.removeItem('mlx_user');
        } catch { /* noop */ }
        // Petit délai pour laisser l'erreur remonter au composant (qui peut afficher un message)
        setTimeout(() => {
          const redirect = encodeURIComponent(path + window.location.search);
          window.location.href = `/login?session=expired&redirect=${redirect}`;
        }, 1200);
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password, mfa_code) => api.post('/api/auth/login', { email, password, mfa_code }),
  me: () => api.get('/api/auth/me'),  // token via l'en-tete Authorization (jamais en URL)
  changePassword: (current_password, new_password) => api.post('/api/auth/change-password', { current_password, new_password }),
  // MFA (TOTP) — compatible Microsoft Authenticator / Google Authenticator / Authy…
  mfaStatus: () => api.get('/api/auth/mfa/status'),
  mfaSetup: () => api.post('/api/auth/mfa/setup'),
  mfaVerify: (code) => api.post('/api/auth/mfa/verify', { code }),
  mfaDisable: (code) => api.post('/api/auth/mfa/disable', { code }),
};

// Dashboard
export const dashboardAPI = {
  get: () => api.get('/api/dashboard'),
};

// Intervenants
export const intervenantsAPI = {
  list: () => api.get('/api/intervenants'),
  get: (id) => api.get(`/api/intervenants/${id}`),
  create: (data) => api.post('/api/intervenants', data),
  update: (id, data) => api.put(`/api/intervenants/${id}`, data),
  delete: (id) => api.delete(`/api/intervenants/${id}`),
};

// Contacts (répertoire externe du cabinet : magistrats, huissiers, confrères… tenant-scopé)
export const contactsAPI = {
  list: (params = {}) => api.get('/api/contacts', { params }),
  stats: () => api.get('/api/contacts/stats'),
  get: (id) => api.get(`/api/contacts/${id}`),
  create: (data) => api.post('/api/contacts', data),
  update: (id, data) => api.put(`/api/contacts/${id}`, data),
  remove: (id) => api.delete(`/api/contacts/${id}`),
};

// Clients
export const clientsAPI = {
  list: (params = {}) => api.get('/api/clients', { params }),
  stats: () => api.get('/api/clients/stats'),
  get: (id) => api.get(`/api/clients/${id}`),
  create: (data) => api.post('/api/clients', data),
  update: (id, data) => api.put(`/api/clients/${id}`, data),
  delete: (id) => api.delete(`/api/clients/${id}`),
  deleteCascade: (id) => api.delete(`/api/clients/${id}?cascade=true`),
  exportCSV: (params = {}) => api.get('/api/clients/export.csv', { params, responseType: 'blob' }),
};

// Dossiers
export const dossiersAPI = {
  list: (params = {}) => api.get('/api/dossiers', { params }),
  get: (id) => api.get(`/api/dossiers/${id}`),
  create: (data) => api.post('/api/dossiers', data),
  update: (id, data) => api.put(`/api/dossiers/${id}`, data),
  delete: (id) => api.delete(`/api/dossiers/${id}`),
  updateStatus: (id, statut) => api.put(`/api/dossiers/${id}/status`, { statut }),
  exportCSV: (params = {}) => api.get('/api/dossiers/export.csv', { params, responseType: 'blob' }),
  // Affaires personnelles : partage de l'accès (propriétaire seul). action: 'ajouter'|'retirer'.
  partager: (id, email, action = 'ajouter') => api.put(`/api/dossiers/${id}/partage`, { email, action }),
  // Détecteur de conflits d'intérêts (assistif) : partie adverse vs clients du cabinet.
  checkConflits: (partie, client_id) => api.get('/api/dossiers/check-conflits', { params: { partie: partie || '', client_id: client_id || '' } }),
};

// Audiences
export const audiencesAPI = {
  list: (params = {}) => api.get('/api/audiences', { params }),
  create: (data) => api.post('/api/audiences', data),
};

// GED — parcourir, alimenter, classer. La recherche plein texte seule ne suffit
// pas : avec 1 400 pièces, il faut pouvoir ENTRER sans deviner un mot-clé.
export const gedAPI = {
  arborescence: () => api.get('/api/ged/arborescence'),
  apercu: () => api.get('/api/ged/apercu'),
  documents: (params = {}) => api.get('/api/ged/documents', { params }),
  suggestions: (id) => api.get(`/api/ged/documents/${id}/suggestions`),
  classer: (data) => api.post('/api/ged/classer', data),
  // Dépôt multiple. `dossier_id` vide = la pièce tombe dans la bannette, ce qui
  // permet de déposer AVANT de savoir où ranger.
  deposer: (fichiers, dossierId = '', typePiece = '', onProgress) => {
    const form = new FormData();
    fichiers.forEach((f) => form.append('fichiers', f));
    form.append('dossier_id', dossierId || '');
    form.append('type_piece', typePiece || '');
    return api.post('/api/ged/depot', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
};

// Rôle d'audience — la feuille que l'avocat refait à la main chaque semaine
// avant d'aller au tribunal.
export const roleAudienceAPI = {
  get: (params = {}) => api.get('/api/role-audience', { params }),
  // `blob` et non un lien direct : l'endpoint exige le jeton, qu'un <a href>
  // n'enverrait pas.
  telechargerPdf: (params = {}) =>
    api.get('/api/role-audience/pdf', { params, responseType: 'blob' }),
};

// Echeances
export const echeancesAPI = {
  list: (params = {}) => api.get('/api/echeances', { params }),
  stats: () => api.get('/api/echeances/stats'),
  create: (data) => api.post('/api/echeances', data),
  updateStatus: (id, statut) => api.put(`/api/echeances/${id}/status?statut=${statut}`),
};

// Tâches (gestion des tâches internes du cabinet, tenant-scopé)
export const tachesAPI = {
  list: (params = {}) => api.get('/api/taches', { params }),
  stats: () => api.get('/api/taches/stats'),
  create: (data) => api.post('/api/taches', data),
  update: (id, data) => api.put(`/api/taches/${id}`, data),
  updateStatus: (id, statut) => api.put(`/api/taches/${id}/status?statut=${encodeURIComponent(statut)}`),
  remove: (id) => api.delete(`/api/taches/${id}`),
};

// Suivi du temps (saisies de temps facturables/non facturables, tenant-scopé)
export const tempsAPI = {
  list: (params = {}) => api.get('/api/temps', { params }),
  stats: (params = {}) => api.get('/api/temps/stats', { params }),
  create: (data) => api.post('/api/temps', data),
  update: (id, data) => api.put(`/api/temps/${id}`, data),
  remove: (id) => api.delete(`/api/temps/${id}`),
  // Facturation au temps passé : prépare un brouillon de facture depuis le temps
  // facturable non encore facturé d'un dossier, puis lie les saisies à la facture émise.
  preparerFacture: (data) => api.post('/api/temps/preparer-facture', data),
  marquerFacturees: (data) => api.post('/api/temps/marquer-facturees', data),
};

// Finance — tableau de bord de pilotage financier (tenant-scopé)
export const financeAPI = {
  dashboard: () => api.get('/api/finance/dashboard'),
  mesAffaires: () => api.get('/api/finance/mes-affaires'),   // vue perso du propriétaire
};

// Factures
export const facturesAPI = {
  list: (params = {}) => api.get('/api/factures', { params }),
  stats: () => api.get('/api/factures/stats'),
  create: (data) => api.post('/api/factures', data),
  setStatut: (id, data) => api.put(`/api/factures/${id}/statut`, data),
  exportCSV: (params = {}) => api.get('/api/factures/export.csv', { params, responseType: 'blob' }),
  downloadPdf: (id) => api.get(`/api/factures/${id}/pdf`, { responseType: 'blob' }),
};

// Contrats
export const contratsAPI = {
  list: (params = {}) => api.get('/api/contrats', { params }),
  stats: () => api.get('/api/contrats/stats'),
  get: (id) => api.get(`/api/contrats/${id}`),
  create: (data) => api.post('/api/contrats', data),
  update: (id, data) => api.put(`/api/contrats/${id}`, data),
  delete: (id) => api.delete(`/api/contrats/${id}`),
};

// AI Chat - Maître Multilex (expert multi-domaines + document upload)
export const aiAPI = {
  chat: (data) => api.post('/api/ai/chat', data),
  conversations: () => api.get('/api/ai/conversations'),
  conversation: (id) => api.get(`/api/ai/conversations/${id}`),
  renameConversation: (id, title) => api.patch(`/api/ai/conversations/${id}`, { title }),
  deleteConversation: (id) => api.delete(`/api/ai/conversations/${id}`),
  capabilities: () => api.get('/api/ai/capabilities'),
  uploadDocument: (file, conversation_id = null) => {
    const fd = new FormData();
    fd.append('file', file);
    if (conversation_id) fd.append('conversation_id', conversation_id);
    return api.post('/api/ai/upload-document', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Maître Adalia en STREAMING : la réponse arrive au fil de l'eau (SSE sur POST).
// EventSource ne sait pas faire de POST -> fetch + lecture manuelle du flux.
// `onDelta(texte)` reçoit chaque fragment, `onStatut(outil)` chaque outil consulté.
// Résout avec l'objet final (même forme que la réponse de aiAPI.chat).
export const streamAiChat = async (payload, { onDelta, onStatut } = {}) => {
  const token = localStorage.getItem('mlx_token');
  const res = await fetch(`${API_BASE}/api/ai/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok || !res.body) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    try { err.detail = (await res.json())?.detail; } catch { /* corps non-JSON */ }
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let tampon = '';
  let fin = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    tampon += decoder.decode(value, { stream: true });
    // Les événements SSE sont séparés par une ligne vide
    const morceaux = tampon.split('\n\n');
    tampon = morceaux.pop();
    for (const morceau of morceaux) {
      const ligne = morceau.split('\n').find(l => l.startsWith('data: '));
      if (!ligne) continue;
      let ev;
      try { ev = JSON.parse(ligne.slice(6)); } catch { continue; }
      if (ev.type === 'delta' && onDelta) onDelta(ev.texte);
      else if (ev.type === 'statut' && onStatut) onStatut(ev.outil);
      else if (ev.type === 'fin') fin = ev;
      else if (ev.type === 'erreur') {
        const err = new Error(ev.detail || 'Erreur IA');
        err.streamed = true;
        throw err;
      }
    }
  }
  if (!fin) {
    const err = new Error('Flux interrompu avant la fin de la réponse');
    err.streamed = true;
    throw err;
  }
  return fin;
};

// Débours refacturables — la dépense d'huissier/greffe saisie sur un dossier
// rejoint enfin la facture du client (motif préparer → facturer → marquer).
export const deboursAPI = {
  aRefacturer: (dossierId) => api.get('/api/cabinet/depenses/a-refacturer', { params: { dossier_id: dossierId } }),
  marquerRefacturees: (depenseIds, factureId) =>
    api.post('/api/cabinet/depenses/marquer-refacturees', { depense_ids: depenseIds, facture_id: factureId || null }),
};

// Chronoscope — chronologie du dossier générée depuis ses pièces (IA, sourcée)
export const chronologieAPI = {
  get: (dossierId) => api.get(`/api/dossiers/${dossierId}/chronologie`),
  // Génération : peut prendre jusqu'à une minute sur un gros dossier.
  generer: (dossierId) => api.post(`/api/dossiers/${dossierId}/chronologie/generer`, {}, { timeout: 120000 }),
};

// Radar à forclusions — les délais présents dans les pièces mais absents de l'agenda
export const radarAPI = {
  get: (dossierId) => api.get(`/api/dossiers/${dossierId}/radar`),
  scanner: (dossierId) => api.post(`/api/dossiers/${dossierId}/radar/scanner`, {}, { timeout: 120000 }),
};

// Documents (GED) — recherche plein texte & téléchargement
export const documentsAPI = {
  // GET /api/documents/search?q=... — recherche dans le contenu des documents du cabinet.
  // Filtres optionnels : type_piece (étiquette), dossier_id.
  search: (query, filtres = {}) => api.get('/api/documents/search', { params: { q: query, ...filtres } }),
  // GET /api/documents/types-piece — étiquettes en usage (menu du filtre)
  typesPiece: () => api.get('/api/documents/types-piece'),
  // GET /api/documents/{id}/download — récupère le binaire (blob) du document
  download: (docId) => api.get(`/api/documents/${docId}/download`, { responseType: 'blob' }),
  // PUT /api/documents/{id}/partage-client — partage (ou retire) la pièce au portail client
  partageClient: (docId, partage) => api.put(`/api/documents/${docId}/partage-client`, { partage }),
};

// Helper : télécharge un document et déclenche le téléchargement navigateur
export const downloadDocument = async (docId, filename) => {
  const res = await documentsAPI.download(docId);
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'document';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Reports
export const reportsAPI = {
  get: (period = '2025') => api.get(`/api/reports?period=${period}`),
};

// Gestion du cabinet — dépenses, employés, salaires, résultat (associés uniquement)
export const cabinetAPI = {
  // Dépenses
  depenses: (params = {}) => api.get('/api/cabinet/depenses', { params }),
  depensesStats: (params = {}) => api.get('/api/cabinet/depenses/stats', { params }),
  createDepense: (data) => api.post('/api/cabinet/depenses', data),
  updateDepense: (id, data) => api.put(`/api/cabinet/depenses/${id}`, data),
  removeDepense: (id) => api.delete(`/api/cabinet/depenses/${id}`),
  // Employés
  employes: (params = {}) => api.get('/api/cabinet/employes', { params }),
  employesStats: () => api.get('/api/cabinet/employes/stats'),
  createEmploye: (data) => api.post('/api/cabinet/employes', data),
  updateEmploye: (id, data) => api.put(`/api/cabinet/employes/${id}`, data),
  removeEmploye: (id) => api.delete(`/api/cabinet/employes/${id}`),
  // Salaires (bulletins de paie)
  salaires: (params = {}) => api.get('/api/cabinet/salaires', { params }),
  salairesStats: (params = {}) => api.get('/api/cabinet/salaires/stats', { params }),
  createSalaire: (data) => api.post('/api/cabinet/salaires', data),
  updateSalaire: (id, data) => api.put(`/api/cabinet/salaires/${id}`, data),
  removeSalaire: (id) => api.delete(`/api/cabinet/salaires/${id}`),
  // Résultat (compte de résultat simplifié)
  resultat: (mois) => api.get('/api/cabinet/resultat', { params: mois ? { mois } : {} }),
};

// Journal
export const journalAPI = {
  list: (params = {}) => api.get('/api/journal', { params }),
};

// Super-Admin — administration de la plateforme (réservé au rôle SuperAdmin)
export const superAdminAPI = {
  organizations: () => api.get('/api/super-admin/organizations'),
  createOrganization: (data) => api.post('/api/super-admin/organizations', data),
  updateOrganization: (id, data) => api.put(`/api/super-admin/organizations/${id}`, data),
  organization: (id) => api.get(`/api/super-admin/organizations/${id}`),
  suspend: (id) => api.post(`/api/super-admin/organizations/${id}/suspend`),
  activate: (id) => api.post(`/api/super-admin/organizations/${id}/activate`),
  stats: (id) => api.get(`/api/super-admin/organizations/${id}/stats`),
  overview: () => api.get('/api/super-admin/overview'),
  users: (id) => api.get(`/api/super-admin/organizations/${id}/users`),
  allUsers: (q = '') => api.get('/api/super-admin/users', { params: q ? { q } : {} }),
  audit: (limit = 25) => api.get('/api/super-admin/audit', { params: { limit } }),
};

// Équipe — membres/comptes du cabinet (tenant-scopé)
export const teamAPI = {
  list: () => api.get('/api/team'),
  create: (data) => api.post('/api/team', data),
  updateRole: (id, role) => api.put(`/api/team/${id}/role`, { role }),
  updateActive: (id, actif) => api.put(`/api/team/${id}/active`, { actif }),
  resetPassword: (id) => api.post(`/api/team/${id}/reset-password`),
};

// Délais procéduraux (catalogue de règles, jours fériés, événements → échéances)
export const delaisAPI = {
  // Catalogue de règles
  reglesList: () => api.get('/api/delais/regles'),
  regleCreate: (data) => api.post('/api/delais/regles', data),
  regleUpdate: (id, data) => api.put(`/api/delais/regles/${id}`, data),
  regleDelete: (id) => api.delete(`/api/delais/regles/${id}`),
  // Jours fériés
  feriesList: () => api.get('/api/delais/feries'),
  ferieCreate: (data) => api.post('/api/delais/feries', data),
  ferieDelete: (id) => api.delete(`/api/delais/feries/${id}`),
  // Référentiel des types d'événements
  eventTypes: () => api.get('/api/delais/evenements/types'),
  // Événements procéduraux par dossier
  evenementsList: (dossierId) => api.get(`/api/dossiers/${dossierId}/evenements`),
  evenementCreate: (dossierId, data) => api.post(`/api/dossiers/${dossierId}/evenements`, data),
  evenementDelete: (id) => api.delete(`/api/delais/evenements/${id}`),
};

// Courriers (correspondance : CRUD + rédaction IA + génération DOCX/PDF)
export const courriersAPI = {
  list: (params = {}) => api.get('/api/courriers', { params }),
  get: (id) => api.get(`/api/courriers/${id}`),
  create: (data) => api.post('/api/courriers', data),
  update: (id, data) => api.put(`/api/courriers/${id}`, data),
  remove: (id) => api.delete(`/api/courriers/${id}`),
  draft: (data) => api.post('/api/courriers/draft', data),
  generate: (id, format) => api.post(`/api/courriers/${id}/generate?format=${format}`, null, { responseType: 'blob' }),
  byDossier: (dossierId) => api.get(`/api/dossiers/${dossierId}/courriers`),
};

// Notifications (fil du cabinet : rappels de délais, etc. — collection persistante)
export const notificationsAPI = {
  list: (limit = 50) => api.get(`/api/notifications?limit=${limit}`),
  unreadCount: () => api.get('/api/notifications/unread_count'),
  read: (id) => api.put(`/api/notifications/${id}/read`),
  readAll: () => api.put('/api/notifications/read-all'),
};

// Actions de l'assistant agentique (proposées par l'IA, confirmées par l'humain)
export const aiActionsAPI = {
  list: (conversationId, statut = 'pending') => api.get('/api/ai/actions', { params: { conversation_id: conversationId, statut } }),
  confirm: (id) => api.post(`/api/ai/actions/${id}/confirm`),
  cancel: (id) => api.post(`/api/ai/actions/${id}/cancel`),
};

// Proactivité (brief du jour déterministe : temps non facturé, dossiers dormants)
export const proactiviteAPI = {
  brief: () => api.get('/api/proactivite/brief'),
};

// ═══════════════════════════════════════════════════════════════════
// Phase 4.1 — Client Portal
// ═══════════════════════════════════════════════════════════════════
// Separate axios instance for portal users (uses mlx_portal_token)
const portalApi = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });
portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('mlx_portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const portalAPI = {
  // Auth
  login: (email, password) => portalApi.post('/api/portail/auth/login', { email, password }),
  setPassword: (invitation_token, password) => portalApi.post('/api/portail/auth/set-password', { invitation_token, password }),
  me: () => portalApi.get('/api/portail/auth/me'),
  // Client-scoped data
  dashboard: () => portalApi.get('/api/portail/dashboard'),
  dossiers: () => portalApi.get('/api/portail/dossiers'),
  dossier: (id) => portalApi.get(`/api/portail/dossiers/${id}`),
  factures: () => portalApi.get('/api/portail/factures'),
  facture: (id) => portalApi.get(`/api/portail/factures/${id}`),
  contrats: () => portalApi.get('/api/portail/contrats'),
  // Pièces partagées par le cabinet sur un dossier (rien d'autre n'est visible)
  documents: (dossierId) => portalApi.get(`/api/portail/dossiers/${dossierId}/documents`),
  downloadDocument: async (docId, filename) => {
    const res = await portalApi.get(`/api/portail/documents/${docId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  // Admin (uses main JWT)
  admin: {
    invite: (client_id, email) => api.post('/api/portail/admin/invite', { client_id, email }),
    listInvitations: () => api.get('/api/portail/admin/invitations'),
    deleteInvitation: (id) => api.delete(`/api/portail/admin/invitations/${id}`),
  },
};

// ═══════════════════════════════════════════════════════════════════
// Phase 4.2 — OHADA Templates
// ═══════════════════════════════════════════════════════════════════
export const templatesAPI = {
  list: (categorie) => api.get('/api/templates', { params: categorie ? { categorie } : {} }),
  get: (id) => api.get(`/api/templates/${id}`),
  create: (data) => api.post('/api/templates', data),
  update: (id, data) => api.put(`/api/templates/${id}`, data),
  delete: (id) => api.delete(`/api/templates/${id}`),
  render: (id, data) => api.post(`/api/templates/${id}/render`, data),
  categories: () => api.get('/api/templates/categories'),
  seedDefaults: () => api.post('/api/templates/seed-defaults'),
  exportDocx: (id, data) => api.post(`/api/templates/${id}/export/docx`, data, { responseType: 'blob' }),
  exportPdf: (id, data) => api.post(`/api/templates/${id}/export/pdf`, data, { responseType: 'blob' }),
};

// Facturation intelligente (Phase 5)
export const billingAPI = {
  status: () => api.get('/api/billing/status'),
  runNow: () => api.post('/api/billing/run-now'),
  upcoming: (days = 30) => api.get('/api/billing/upcoming', { params: { days } }),
  recurringPlans: () => api.get('/api/billing/recurring-plans'),
  runs: (limit = 10) => api.get('/api/billing/runs', { params: { limit } }),
  generateFromContract: (contractId) => api.post(`/api/billing/contracts/${contractId}/generate-invoice`),
  markPaid: (invoiceId, modePaiement = 'Virement') =>
    api.post(`/api/billing/invoices/${invoiceId}/mark-paid`, null, { params: { mode_paiement: modePaiement } }),
  sendReminder: (invoiceId) => api.post(`/api/billing/invoices/${invoiceId}/send-reminder`),
};

// ═══════════════════════════════════════════════════════════════════
// Phase 4.3 — Microsoft 365 Calendar
// ═══════════════════════════════════════════════════════════════════
export const calendarAPI = {
  status: () => api.get('/api/calendar/status'),
  events: (start, end) => api.get('/api/calendar/events', { params: { start, end } }),
  createEvent: (data) => api.post('/api/calendar/events', data),
  deleteEvent: (event_id) => api.delete(`/api/calendar/events/${event_id}`),
  sync: (data = {}) => api.post('/api/calendar/sync', data),
};

// ═══════════════════════════════════════════════════════════════════
// Conventions d'honoraires — le cabinet travaille au FORFAIT NÉGOCIÉ.
// Le mode horaire reste disponible dossier par dossier (type « horaire »).
// ═══════════════════════════════════════════════════════════════════
export const honorairesAPI = {
  // Conventions
  list: (params = {}) => api.get('/api/honoraires/conventions', { params }),
  get: (id) => api.get(`/api/honoraires/conventions/${id}`),
  create: (data) => api.post('/api/honoraires/conventions', data),
  update: (id, data) => api.put(`/api/honoraires/conventions/${id}`, data),
  remove: (id) => api.delete(`/api/honoraires/conventions/${id}`),

  // La convention à imprimer et faire signer. Passe par axios en `blob` — et non
  // par un lien direct : l'endpoint exige le jeton, qu'un <a href> n'enverrait
  // pas (le navigateur recevrait un 401 au lieu du document).
  telechargerPdf: (id) =>
    api.get(`/api/honoraires/conventions/${id}/pdf`, { responseType: 'blob' }),

  // Un jalon atteint devient une facture (on n'appelle pas du temps, on appelle
  // une tranche convenue d'avance).
  appelerTranche: (id, data = {}) =>
    api.post(`/api/honoraires/conventions/${id}/appeler-tranche`, data),

  // Montant RÉELLEMENT recouvré — base des honoraires de résultat.
  declarerAssiette: (id, data) => api.post(`/api/honoraires/conventions/${id}/assiette`, data),

  // Pilotage
  rentabilite: (dossierId) => api.get(`/api/honoraires/dossiers/${dossierId}/rentabilite`),
  portefeuille: () => api.get('/api/honoraires/portefeuille'),
  trames: () => api.get('/api/honoraires/trames'),

  // Ce que devraient coûter des dossiers comparables — pour cesser de
  // négocier à l'instinct.
  devis: (params = {}) => api.get('/api/honoraires/devis', { params }),

  // Budget-temps : prévenir pendant le dossier, au lieu de constater après.
  derive: (dossierId, params = {}) =>
    api.get(`/api/honoraires/dossiers/${dossierId}/derive`, { params }),
  derivesEnCours: (params = {}) => api.get('/api/honoraires/derives', { params }),
};

export default api;
